import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

// Import components
import Login from './components/auth/Login';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ChangePassword from './components/auth/ChangePassword';
import Dashboard from './components/dashboard/Dashboard';
import CustomerGeography from './components/analytics/CustomerGeography';
import Layout from './components/layout/Layout';

// Import page components
import Users from './components/Users';
import FlaggedUsers from './components/FlaggedUsers';
import Support from './components/Support';
import LiveUsers from './components/LiveUsers';
import SubscriptionPlans from './components/SubscriptionPlans';

// Import App Assets components
import Positions from './components/app-assets/Positions';
import BodyTypes from './components/app-assets/BodyTypes';
import Tags from './components/app-assets/Tags';
import LookingFor from './components/app-assets/LookingFor';
import RelationshipStatuses from './components/app-assets/RelationshipStatuses';

// Import Legal Docs components
import PrivacyPolicy from './components/legal-docs/PrivacyPolicy';
import TermsConditions from './components/legal-docs/TermsConditions';
import CommunityGuidelines from './components/legal-docs/CommunityGuidelines';
import ChildSafetyStandards from './components/legal-docs/ChildSafetyStandards';

// Import Communication components
import SendEmail from './components/communication/SendEmail';
import SendPush from './components/communication/SendPush';

// Import System components
import GoogleConsole from './components/system/GoogleConsole';
import AppCredentials from './components/system/AppCredentials';
import PaymentSettings from './components/system/PaymentSettings';
import AppVersionSettings from './components/system/AppVersionSettings';

// Import Website components
import DeleteAccountRequests from './components/website/DeleteAccountRequests';
import ContactRequests from './components/website/ContactRequests';
import ReportIssueRequests from './components/website/ReportIssueRequests';

// Import Rewards components
import RewardSettings from './components/rewards/RewardSettings';
import PromoCodes from './components/rewards/PromoCodes';

// Import Premium components
import PremiumUsers from './components/premium/PremiumUsers';

// Import Chat component
import Chat from './pages/Chat';

// Import context
import { AuthProvider, useAuth } from './contexts/AuthContext';

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  console.log('🛡️ ProtectedRoute - isLoading:', isLoading, 'isAuthenticated:', isAuthenticated);
  
  if (isLoading) {
    console.log('⏳ ProtectedRoute - Still loading, showing spinner');
    return <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }
  
  if (!isAuthenticated) {
    console.log('🔒 ProtectedRoute - User not authenticated, redirecting to login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('✅ ProtectedRoute - User authenticated, showing protected content');
  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
      <div className="spinner-border text-primary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>;
  }
  
  if (isAuthenticated) {
    console.log('🔓 User already authenticated, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }
  
  return <>{children}</>;
};

function App() {
  console.log('🚀 App component rendering, current URL:', window.location.pathname);
  
  return (
    <AuthProvider>
      <Router>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/forgot-password" element={
              <PublicRoute>
                <ForgotPassword />
              </PublicRoute>
            } />
            <Route path="/reset-password/:token" element={
              <PublicRoute>
                <ResetPassword />
              </PublicRoute>
            } />

            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/analytics/customer-geography" element={
              <ProtectedRoute>
                <Layout>
                  <CustomerGeography />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Change Password */}
            <Route path="/change-password" element={
              <ProtectedRoute>
                <Layout>
                  <ChangePassword />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Single Pages */}
            <Route path="/users" element={
              <ProtectedRoute>
                <Users />
              </ProtectedRoute>
            } />
            <Route path="/chat/:userId" element={
              <ProtectedRoute>
                <Chat />
              </ProtectedRoute>
            } />
            <Route path="/flagged-users" element={
              <ProtectedRoute>
                <FlaggedUsers />
              </ProtectedRoute>
            } />
            <Route path="/support" element={
              <ProtectedRoute>
                <Support />
              </ProtectedRoute>
            } />
            <Route path="/live-users" element={
              <ProtectedRoute>
                <LiveUsers />
              </ProtectedRoute>
            } />
            <Route path="/subscription-plans" element={
              <ProtectedRoute>
                <SubscriptionPlans />
              </ProtectedRoute>
            } />

            {/* App Assets Routes */}
            <Route path="/app-assets/positions" element={
              <ProtectedRoute>
                <Positions />
              </ProtectedRoute>
            } />
            <Route path="/app-assets/body-types" element={
              <ProtectedRoute>
                <BodyTypes />
              </ProtectedRoute>
            } />
            <Route path="/app-assets/looking-for" element={
              <ProtectedRoute>
                <LookingFor />
              </ProtectedRoute>
            } />
            <Route path="/app-assets/relationship-statuses" element={
              <ProtectedRoute>
                <RelationshipStatuses />
              </ProtectedRoute>
            } />
            <Route path="/app-assets/tags" element={
              <ProtectedRoute>
                <Tags />
              </ProtectedRoute>
            } />

            {/* Legal Docs Routes */}
            <Route path="/legal-docs/privacy-policy" element={
              <ProtectedRoute>
                <PrivacyPolicy />
              </ProtectedRoute>
            } />
            <Route path="/legal-docs/terms-conditions" element={
              <ProtectedRoute>
                <TermsConditions />
              </ProtectedRoute>
            } />
            <Route path="/legal-docs/community-guidelines" element={
              <ProtectedRoute>
                <CommunityGuidelines />
              </ProtectedRoute>
            } />
            <Route path="/legal-docs/child-safety-standards" element={
              <ProtectedRoute>
                <ChildSafetyStandards />
              </ProtectedRoute>
            } />

            {/* Website Routes */}
            <Route path="/website/delete-account-requests" element={
              <ProtectedRoute>
                <DeleteAccountRequests />
              </ProtectedRoute>
            } />
            <Route path="/website/contact-requests" element={
              <ProtectedRoute>
                <ContactRequests />
              </ProtectedRoute>
            } />
            <Route path="/website/report-issue-requests" element={
              <ProtectedRoute>
                <ReportIssueRequests />
              </ProtectedRoute>
            } />

            {/* Communication Routes */}
            <Route path="/communication/send-email" element={
              <ProtectedRoute>
                <SendEmail />
              </ProtectedRoute>
            } />
            <Route path="/communication/send-push" element={
              <ProtectedRoute>
                <SendPush />
              </ProtectedRoute>
            } />

            {/* System Routes */}
            <Route path="/system/google-console" element={
              <ProtectedRoute>
                <Layout>
                  <GoogleConsole />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/system/app-credentials" element={
              <ProtectedRoute>
                <Layout>
                  <AppCredentials />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/system/payment-settings" element={
              <ProtectedRoute>
                <Layout>
                  <PaymentSettings />
                </Layout>
              </ProtectedRoute>
            } />
            <Route path="/system/app-version-settings" element={
              <ProtectedRoute>
                <Layout>
                  <AppVersionSettings />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Rewards Routes */}
            <Route path="/rewards/settings" element={
              <ProtectedRoute>
                <RewardSettings />
              </ProtectedRoute>
            } />
            <Route path="/rewards/promo-codes" element={
              <ProtectedRoute>
                <PromoCodes />
              </ProtectedRoute>
            } />
            <Route path="/premium/users" element={
              <ProtectedRoute>
                <Layout>
                  <PremiumUsers />
                </Layout>
              </ProtectedRoute>
            } />

            {/* Default redirect - only redirect if no other route matches */}
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  );
}

export default App;